import { useEffect, useRef, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.error || "");
  const [notice, setNotice] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const authChecked = useRef(false);

  useEffect(() => {
    if (authChecked.current) return;
    authChecked.current = true;
    api.get("/auth/me").then(() => navigate("/admin/dashboard", { replace: true })).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      localStorage.removeItem("oep_token");
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Accesso non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setNotice(data.message);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail, "Invio recupero non riuscito."));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/admin`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-void px-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm border border-line bg-surface p-10">
        <div className="flex justify-center">
          <img src="/media/logo-full.png" alt="Logo Orologi e Preziosi" className="h-24 w-24 object-contain" data-testid="admin-logo-image" />
        </div>
        <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-muted">
          {forgotMode ? "Recupero Password" : "Area Riservata"}
        </p>

        {forgotMode ? (
          <form onSubmit={handleForgotPassword} className="mt-10 space-y-5">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-forgot-email-input"
                className="w-full border border-line bg-transparent px-4 py-3 text-sm text-ivory outline-none transition-colors duration-300 focus:border-gold"
                autoComplete="email"
              />
            </div>
            {notice && <p data-testid="admin-forgot-success" className="text-xs leading-relaxed text-gold">{notice}</p>}
            {error && <p data-testid="admin-forgot-error" className="text-xs text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-forgot-submit"
              className="w-full border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void disabled:opacity-50"
            >
              {loading ? "Invio…" : "Invia link di recupero"}
            </button>
            <button
              type="button"
              onClick={() => {
                setForgotMode(false);
                setError("");
                setNotice("");
              }}
              data-testid="admin-forgot-back-button"
              className="w-full text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
            >
              Torna al login
            </button>
          </form>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="mt-10 space-y-5">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="admin-email-input"
                  className="w-full border border-line bg-transparent px-4 py-3 text-sm text-ivory outline-none transition-colors duration-300 focus:border-gold"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-muted">Password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="admin-password-input"
                  className="w-full border border-line bg-transparent px-4 py-3 text-sm text-ivory outline-none transition-colors duration-300 focus:border-gold"
                  autoComplete="current-password"
                />
              </div>
              {error && (
                <p data-testid="admin-login-error" className="text-xs text-red-400">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="w-full border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors duration-500 hover:bg-gold hover:text-void disabled:opacity-50"
              >
                {loading ? "Accesso…" : "Accedi"}
              </button>
            </form>

            <button
              type="button"
              onClick={() => {
                setForgotMode(true);
                setError("");
              }}
              data-testid="admin-forgot-password-link"
              className="mt-4 w-full text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold"
            >
              Password dimenticata?
            </button>

            <div className="mt-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-line" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">oppure</span>
              <span className="h-px flex-1 bg-line" />
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              data-testid="admin-google-login-button"
              className="mt-6 flex w-full items-center justify-center gap-3 border border-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-gold/10 hover:text-gold disabled:cursor-wait disabled:opacity-60"
            >
              <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.2h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.7Z" />
                <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5L1.4 17.3C3.5 21.4 7.4 24 12 24Z" />
                <path fill="#FBBC05" d="M5.2 14.4c-.3-.8-.4-1.6-.4-2.4s.2-1.6.4-2.4L1.4 6.7C.5 8.5 0 10.2 0 12s.5 3.5 1.4 5.3l3.8-2.9Z" />
                <path fill="#EA4335" d="M12 4.6c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1 15.2 0 12 0 7.4 0 3.5 2.6 1.4 6.7l3.8 2.9c.9-2.9 3.6-5 6.8-5Z" />
              </svg>
              {googleLoading ? "Reindirizzamento…" : "Continua con Google"}
            </button>
          </>
        )}

        <Link to="/" data-testid="admin-back-home" className="mt-8 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors duration-300 hover:text-gold">
          ← Torna al sito
        </Link>
      </div>
    </div>
  );
}
